import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  type SubmitHandler,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  NJButton,
  NJFormItem,
  NJHeading,
  NJIconButton,
  NJInlineMessage,
  NJMultiSelect,
  NJTab,
  NJTabs,
  NJSegmentedControl,
  NJSegmentedControlButton,
  NJSpinner,
  NJText,
  NJToggle,
} from '@engie-group/fluid-design-system-react';
import type { Edge, Node } from '@xyflow/react';
import { Panel } from '@xyflow/react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import {
  NODE_TYPES,
  ProductionEditorCanvas,
  ProductionEditorOverlaySidebar,
  ProductionEditorProvider,
  type ComponentItem,
  type IBaseData,
  type PlantItem,
} from '@shared/production-editor';
import { useCreateAsset, useProductionEditorContext } from './hooks';
import type { AssetCreateFormValues, AssetType } from './types';

export const meta = {
  title: "Création d'un asset — Éditeur de production",
  description:
    "Modale latérale droite de création d'un asset (chaudière par défaut) dans l'éditeur de production : type d'asset, nom, plage de production, contrats combustibles, courbe puissance/rendement éditable et options avancées masquées par toggle. Référence : kit production-editor / view create-asset-modal.",
  tags: ['production-editor', 'modal', 'form', 'asset', 'CPR-asset-create'],
  author: 'cecile.onno',
  status: 'draft' as const,
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  boiler: 'Chaudière',
  'heat-pump': 'Pompe à chaleur',
  storage: 'Stockage',
  solar: 'Solaire thermique',
};

const DEFAULT_VALUES: AssetCreateFormValues = {
  assetType: 'boiler',
  name: '',
  minProduction: 0,
  maxProduction: 10,
  priorityOrder: null,
  fuelContractIds: [],
  curve: [
    { power: 0.01, efficiency: 1 },
    { power: 10, efficiency: 1 },
  ],
  showAdvanced: false,
  co2Emissions: 0,
  warmStartTime: 0,
  startCost: 0,
};

const PLANTS: PlantItem[] = [
  { id: 'constance', label: 'Chaufferie Constance' },
  { id: 'apee', label: 'Chaufferie APEE' },
  { id: 'zac', label: 'Chaufferie ZAC' },
];

const COMPONENTS: ComponentItem[] = [
  {
    id: 'c-departure',
    label: 'Départ',
    entityName: 'Departure',
    icon: 'arrow_forward',
    data: {
      entityLabel: 'Départ',
      nodeKind: 'asset' satisfies NodeKind,
      componentGroup: 'Départ/Retour',
    },
  },
  {
    id: 'c-return',
    label: 'Retour',
    entityName: 'Return',
    icon: 'arrow_back',
    data: {
      entityLabel: 'Retour',
      nodeKind: 'asset' satisfies NodeKind,
      componentGroup: 'Départ/Retour',
    },
  },
  {
    id: 'c-boiler',
    label: 'Chaudière',
    entityName: 'Boiler',
    icon: 'local_fire_department',
    data: {
      entityLabel: 'Chaudière',
      nodeKind: 'asset' satisfies NodeKind,
      componentGroup: 'Moyens de production',
    },
  },
  {
    id: 'c-chiller',
    label: 'Refroidisseur',
    entityName: 'Chiller',
    icon: 'ac_unit',
    data: {
      entityLabel: 'Refroidisseur',
      nodeKind: 'asset' satisfies NodeKind,
      componentGroup: 'Moyens de production',
    },
  },
  {
    id: 'c-storage',
    label: 'Stockage',
    entityName: 'WaterStorage',
    icon: 'inventory_2',
    data: {
      entityLabel: 'Stockage',
      nodeKind: 'asset' satisfies NodeKind,
      componentGroup: 'Moyens de production',
    },
  },
  {
    id: 'c-pump-group',
    label: 'Groupe de pompes',
    entityName: 'PumpGroup',
    icon: 'view_module',
    data: {
      entityLabel: 'Groupe de pompes',
      nodeKind: 'group' satisfies NodeKind,
      componentGroup: 'Pompes',
    },
  },
  {
    id: 'c-fuel-contract',
    label: 'Contrat combustible',
    entityName: 'FuelContract',
    icon: 'local_gas_station',
    data: {
      entityLabel: 'Contrat combustible',
      nodeKind: 'contract' satisfies NodeKind,
      componentGroup: "Source d'énergie",
    },
  },
];

type NodeKind = 'asset' | 'contract' | 'group';

const EDGE_RED = { stroke: '#E1352B', strokeWidth: 3 };
const EDGE_GREY_DASHED = {
  stroke: 'var(--nj-core-color-grey-400)',
  strokeWidth: 1,
  strokeDasharray: '4 4',
};

function buildInitialGraph(): { nodes: Node<IBaseData>[]; edges: Edge[] } {
  const nodes: Node<IBaseData>[] = [
    {
      id: 'fuel-gaz',
      type: 'contract',
      position: { x: 260, y: 20 },
      data: {
        entityName: 'FuelContract',
        entityLabel: 'Contrat combustible',
        label: 'Gaz quotas Constance',
      },
      width: 170,
      height: 130,
    },
    {
      id: 'fuel-bio',
      type: 'contract',
      position: { x: 470, y: 20 },
      data: {
        entityName: 'FuelContract',
        entityLabel: 'Contrat combustible',
        label: 'Biomasse Constance',
      },
      width: 170,
      height: 130,
    },
    {
      id: 'boiler-gaz',
      type: 'asset',
      position: { x: 260, y: 220 },
      data: {
        entityName: 'Boiler',
        entityLabel: 'Chaudière',
        label: 'Gaz Constance',
      },
      width: 170,
      height: 150,
    },
    {
      id: 'boiler-bio',
      type: 'asset',
      position: { x: 470, y: 220 },
      data: {
        entityName: 'Boiler',
        entityLabel: 'Chaudière',
        label: 'Biomasse Constance',
      },
      width: 170,
      height: 150,
    },
    {
      id: 'collector-1',
      type: 'asset',
      position: { x: 735, y: 250 },
      data: {
        entityName: 'Collector',
        entityLabel: 'Collecteur',
        label: 'Collecteur principal',
      },
      width: 130,
      height: 90,
    },
    {
      id: 'pg-zac',
      type: 'group',
      position: { x: 955, y: 205 },
      data: {
        entityName: 'PumpGroup',
        entityLabel: 'Groupe de pompes',
        label: 'Groupe ZAC',
      },
      style: { width: 280, height: 220 },
    },
    {
      id: 'pump-zac',
      type: 'groupMember',
      parentId: 'pg-zac',
      extent: 'parent',
      position: { x: 30, y: 55 },
      data: {
        entityName: 'VariableSpeedPump',
        entityLabel: 'Pompe à vitesse variable',
        label: 'Pompe ZAC',
      },
      width: 220,
      height: 130,
    },
    {
      id: 'departure-zac',
      type: 'asset',
      position: { x: 1285, y: 240 },
      data: {
        entityName: 'Departure',
        entityLabel: 'Départ',
        label: 'Départ ZAC',
      },
      width: 140,
      height: 150,
    },
  ];

  const edges: Edge[] = [
    {
      id: 'e-fgaz-bgaz',
      source: 'fuel-gaz',
      target: 'boiler-gaz',
      type: 'step',
      style: EDGE_GREY_DASHED,
      sourceHandle: 'bottom',
      targetHandle: 'top',
    },
    {
      id: 'e-fbio-bbio',
      source: 'fuel-bio',
      target: 'boiler-bio',
      type: 'step',
      style: EDGE_GREY_DASHED,
      sourceHandle: 'bottom',
      targetHandle: 'top',
    },
    {
      id: 'e-bgaz-coll',
      source: 'boiler-gaz',
      target: 'collector-1',
      type: 'step',
      style: EDGE_RED,
      sourceHandle: 'right',
      targetHandle: 'left',
    },
    {
      id: 'e-bbio-coll',
      source: 'boiler-bio',
      target: 'collector-1',
      type: 'step',
      style: EDGE_RED,
      sourceHandle: 'right',
      targetHandle: 'left',
    },
    {
      id: 'e-coll-pgzac',
      source: 'collector-1',
      target: 'pg-zac',
      type: 'step',
      style: EDGE_RED,
      sourceHandle: 'right',
      targetHandle: 'left',
    },
    {
      id: 'e-pgzac-dep',
      source: 'pg-zac',
      target: 'departure-zac',
      type: 'step',
      style: EDGE_RED,
      sourceHandle: 's-right',
      targetHandle: 'left',
    },
  ];

  return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Yup schema
// ---------------------------------------------------------------------------

const schema = yup.object({
  assetType: yup
    .mixed<AssetType>()
    .oneOf(['boiler', 'heat-pump', 'storage', 'solar'])
    .required(),
  name: yup
    .string()
    .trim()
    .required('Le nom est obligatoire')
    .max(64, 'Maximum 64 caractères'),
  minProduction: yup
    .number()
    .typeError('Valeur numérique requise')
    .min(0, 'Doit être ≥ 0')
    .required(),
  maxProduction: yup
    .number()
    .typeError('Valeur numérique requise')
    .moreThan(
      yup.ref('minProduction'),
      'Doit être strictement supérieur au min',
    )
    .required(),
  priorityOrder: yup
    .number()
    .typeError('Valeur numérique requise')
    .integer('Entier requis')
    .min(0, 'Doit être ≥ 0')
    .nullable()
    .transform((value, original) =>
      original === '' || original === null ? null : value,
    ),
  fuelContractIds: yup
    .array(yup.string().required())
    .min(1, 'Sélectionnez au moins un contrat')
    .required(),
  curve: yup
    .array()
    .of(
      yup
        .object({
          power: yup
            .number()
            .typeError('Numérique')
            .min(0, '≥ 0')
            .required(),
          efficiency: yup
            .number()
            .typeError('Numérique')
            .moreThan(0, '> 0')
            .max(1.5, '≤ 1.5')
            .required(),
        })
        .required(),
    )
    .min(2, 'Au moins 2 points')
    .required(),
  showAdvanced: yup.boolean().required(),
  co2Emissions: yup.number().typeError('Numérique').min(0, '≥ 0').required(),
  warmStartTime: yup.number().typeError('Numérique').min(0, '≥ 0').required(),
  startCost: yup.number().typeError('Numérique').min(0, '≥ 0').required(),
});

// ---------------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------------

export default function ProductionAssetCreate() {
  const { data, isLoading, error } = useProductionEditorContext();
  const [isOpen, setIsOpen] = useState(true);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [lastDroppedNodeLabel, setLastDroppedNodeLabel] = useState<string | null>(null);
  const [plantActionMessage, setPlantActionMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('editor');
  const [selectedPlantId, setSelectedPlantId] = useState<string | null>('constance');
  const initialGraph = useMemo(() => buildInitialGraph(), []);

  const nodeFactory = useCallback(
    ({
      id,
      position,
      draggedType,
      label,
      data,
    }: {
      id: string;
      position: { x: number; y: number };
      draggedType: string;
      label: string;
      data?: Record<string, unknown>;
    }): Node<IBaseData> => {
      const nodeKind = (data?.nodeKind as NodeKind | undefined) ?? 'asset';
      const entityLabel = (data?.entityLabel as string | undefined) ?? label;
      const baseData: IBaseData = {
        entityName: draggedType,
        entityLabel,
        label,
        onEditClick: () => setIsOpen(true),
      };

      setLastDroppedNodeLabel(label);

      if (nodeKind === 'group') {
        return {
          id,
          type: 'group',
          position,
          data: baseData,
          style: { width: 280, height: 220 },
        };
      }

      if (nodeKind === 'contract') {
        return {
          id,
          type: 'contract',
          position,
          data: baseData,
          width: 170,
          height: 130,
        };
      }

      return {
        id,
        type: 'asset',
        position,
        data: baseData,
        width: 170,
        height: 150,
      };
    },
    [],
  );

  return (
    <PrototypeStudioLayout title={meta.title}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 56px)',
          fontFamily: 'var(--nj-core-font-family-sans)',
          background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
        }}
      >
        <div
          style={{
            background: 'white',
            borderBottom: '1px solid var(--nj-core-color-grey-300)',
            padding: '0 24px',
          }}
        >
          <NJTabs
            label="Sections éditeur de production"
            activeTab={activeTab}
            onClickTabItem={(id) => setActiveTab(id)}
          >
            <NJTab
              id="editor"
              label="Production Editor"
              ariaControls="panel-editor"
              isActive={activeTab === 'editor'}
            />
            <NJTab
              id="contracts"
              label="Fuels"
              ariaControls="panel-contracts"
              isActive={activeTab === 'contracts'}
            />
            <NJTab
              id="synthesis"
              label="Synthesis"
              ariaControls="panel-synthesis"
              isActive={activeTab === 'synthesis'}
            />
          </NJTabs>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {activeTab === 'editor' ? (
            <ProductionEditorProvider
              initialNodes={initialGraph.nodes}
              initialEdges={initialGraph.edges}
            >
              <ProductionEditorCanvas
                nodeTypes={NODE_TYPES}
                minHeight="100%"
                nodeFactory={nodeFactory}
              >
                <ProductionEditorOverlaySidebar
                  plants={PLANTS}
                  components={COMPONENTS}
                  selectedPlantId={selectedPlantId}
                  onSelectPlant={setSelectedPlantId}
                  onCreatePlant={() =>
                    setPlantActionMessage(
                      'Création de centrale déclenchée (prototype).',
                    )
                  }
                />

                <Panel position="top-right">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <NJButton
                      emphasis="bold"
                      icon="add"
                      label="Créer un asset"
                      onClick={() => setIsOpen(true)}
                    />
                    {confirmation && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          maxWidth: 520,
                        }}
                      >
                        <NJInlineMessage variant="success">
                          {confirmation}
                        </NJInlineMessage>
                        <NJIconButton
                          icon="close"
                          label="Fermer la confirmation"
                          variant="tertiary"
                          scale="sm"
                          onClick={() => setConfirmation(null)}
                        />
                      </div>
                    )}
                    {lastDroppedNodeLabel && (
                      <NJInlineMessage variant="information">
                        {`Composant « ${lastDroppedNodeLabel} » ajouté. Cliquez le nœud dans le schéma pour le configurer.`}
                      </NJInlineMessage>
                    )}
                    {plantActionMessage && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <NJInlineMessage variant="information">
                          {plantActionMessage}
                        </NJInlineMessage>
                        <NJIconButton
                          icon="close"
                          label="Fermer le message"
                          variant="tertiary"
                          scale="sm"
                          onClick={() => setPlantActionMessage(null)}
                        />
                      </div>
                    )}
                  </div>
                </Panel>
              </ProductionEditorCanvas>
            </ProductionEditorProvider>
          ) : (
            <div style={{ padding: 24 }}>
              <NJInlineMessage variant="information">
                Cette variante se concentre sur la création d&apos;asset depuis
                l&apos;onglet Production Editor.
              </NJInlineMessage>
            </div>
          )}
        </div>
      </div>

      {isOpen && data && (
        <AssetCreateModal
          context={data}
          onClose={() => setIsOpen(false)}
          onSuccess={(name) => {
            setConfirmation(`Asset « ${name} » créé avec succès.`);
            setIsOpen(false);
          }}
        />
      )}

      {isOpen && isLoading && (
        <BackdropSpinner label="Chargement du contexte de l'éditeur…" />
      )}
      {isOpen && error && (
        <BackdropError message="Impossible de charger le contexte." />
      )}
    </PrototypeStudioLayout>
  );
}

// ---------------------------------------------------------------------------
// Modal (right side panel)
// ---------------------------------------------------------------------------

interface AssetCreateModalProps {
  context: NonNullable<ReturnType<typeof useProductionEditorContext>['data']>;
  onClose: () => void;
  onSuccess: (name: string) => void;
}

function AssetCreateModal({ context, onClose, onSuccess }: AssetCreateModalProps) {
  const createMutation = useCreateAsset();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AssetCreateFormValues>({
    defaultValues: DEFAULT_VALUES,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema as any),
    mode: 'onBlur',
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'curve' });
  const showAdvanced = watch('showAdvanced');
  const assetType = watch('assetType');

  const title = useMemo(
    () => `Créer ${assetTypeArticle(assetType)} ${ASSET_TYPE_LABELS[assetType].toLowerCase()}`,
    [assetType],
  );

  // Lock body scroll while modal is open
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const onSubmit: SubmitHandler<AssetCreateFormValues> = async (values) => {
    const result = await createMutation.mutateAsync(values);
    onSuccess(result.name);
  };

  return (
    <div
      role="dialog"
      aria-modal
      aria-labelledby="asset-create-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1100,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '480px',
          maxWidth: '95vw',
          background:
            'var(--nj-semantic-color-background-neutral-primary-default)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'var(--nj-core-font-family-sans)',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.18)',
        }}
      >
        <header
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--nj-core-color-grey-200)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <NJHeading id="asset-create-title" scale="md">
            {title}
          </NJHeading>
          <NJIconButton
            icon="close"
            label="Fermer"
            variant="tertiary"
            scale="md"
            onClick={onClose}
          />
        </header>

        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            {/* ---- Asset type ---- */}
            <Controller
              control={control}
              name="assetType"
              render={({ field }) => (
                <NJSegmentedControl
                  label="Type d'asset"
                  size="sm"
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                >
                  {(Object.keys(ASSET_TYPE_LABELS) as AssetType[]).map((t) => (
                    <NJSegmentedControlButton
                      key={t}
                      value={t}
                      label={ASSET_TYPE_LABELS[t]}
                    />
                  ))}
                </NJSegmentedControl>
              )}
            />

            {/* ---- Name ---- */}
            <NJFormItem
              id="asset-name"
              label="Nom"
              type="text"
              isRequired
              hasError={!!errors.name}
              subscriptMessage={errors.name?.message}
              {...register('name')}
            />

            {/* ---- Min / max production ---- */}
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <NJFormItem
                  id="asset-min"
                  label="Production minimale (MW)"
                  type="number"
                  isRequired
                  hasError={!!errors.minProduction}
                  subscriptMessage={errors.minProduction?.message}
                  {...register('minProduction', { valueAsNumber: true })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <NJFormItem
                  id="asset-max"
                  label="Production maximale (MW)"
                  type="number"
                  isRequired
                  hasError={!!errors.maxProduction}
                  subscriptMessage={errors.maxProduction?.message}
                  {...register('maxProduction', { valueAsNumber: true })}
                />
              </div>
            </div>

            {/* ---- Priority ---- */}
            <NJFormItem
              id="asset-priority"
              label="Ordre de priorité (optionnel)"
              type="number"
              hasError={!!errors.priorityOrder}
              subscriptMessage={errors.priorityOrder?.message}
              {...register('priorityOrder', {
                setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
              })}
            />

            {/* ---- Fuel contracts ---- */}
            <Controller
              control={control}
              name="fuelContractIds"
              render={({ field }) => (
                <NJMultiSelect.Root
                  id="asset-fuel-contracts"
                  label="Contrats combustibles"
                  listNavigationLabel="Utiliser les flèches et Entrée pour cocher"
                  value={field.value}
                  onChange={(values) => field.onChange(values)}
                  hasError={!!errors.fuelContractIds}
                  subscriptMessage={
                    errors.fuelContractIds?.message as string | undefined
                  }
                >
                  {context.fuelContracts.map((c) => (
                    <NJMultiSelect.Item key={c.id} value={c.id}>
                      {c.name}
                    </NJMultiSelect.Item>
                  ))}
                </NJMultiSelect.Root>
              )}
            />

            {/* ---- Curve ---- */}
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <NJText scale="sm" style={{ fontWeight: 600 }}>
                  Courbe puissance / rendement
                </NJText>
                <div style={{ display: 'flex', gap: 4 }}>
                  <NJIconButton
                    icon="add"
                    label="Ajouter un point"
                    variant="tertiary"
                    scale="sm"
                    onClick={() => append({ power: 0, efficiency: 1 })}
                  />
                  <NJIconButton
                    icon="remove"
                    label="Retirer le dernier point"
                    variant="tertiary"
                    scale="sm"
                    isDisabled={fields.length <= 2}
                    onClick={() => remove(fields.length - 1)}
                  />
                </div>
              </div>
              <CurveTable
                fields={fields}
                register={register}
                errors={errors.curve}
                onRemoveRow={(i) => fields.length > 2 && remove(i)}
              />
              {errors.curve?.message && (
                <NJInlineMessage variant="error">
                  {errors.curve.message}
                </NJInlineMessage>
              )}
            </div>

            {/* ---- Advanced toggle ---- */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Controller
                control={control}
                name="showAdvanced"
                render={({ field }) => (
                  <NJToggle
                    checked={!!field.value}
                    onChange={(_, checked) => field.onChange(!!checked)}
                    aria-label="Afficher les options avancées"
                  />
                )}
              />
              <NJText scale="sm">Afficher les options avancées</NJText>
            </div>

            {showAdvanced && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  padding: 16,
                  background:
                    'var(--nj-semantic-color-background-neutral-secondary-default)',
                  borderRadius: 6,
                  border: '1px solid var(--nj-core-color-grey-200)',
                }}
              >
                <NJFormItem
                  id="asset-co2"
                  label="Émissions CO₂ (kg/MWh)"
                  type="number"
                  hasError={!!errors.co2Emissions}
                  subscriptMessage={errors.co2Emissions?.message}
                  {...register('co2Emissions', { valueAsNumber: true })}
                />
                <NJFormItem
                  id="asset-warmstart"
                  label="Temps de démarrage à chaud (s)"
                  type="number"
                  hasError={!!errors.warmStartTime}
                  subscriptMessage={errors.warmStartTime?.message}
                  {...register('warmStartTime', { valueAsNumber: true })}
                />
                <NJFormItem
                  id="asset-startcost"
                  label="Coût de démarrage (€)"
                  type="number"
                  hasError={!!errors.startCost}
                  subscriptMessage={errors.startCost?.message}
                  {...register('startCost', { valueAsNumber: true })}
                />
              </div>
            )}
          </div>

          {/* ---- Footer ---- */}
          <footer
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--nj-core-color-grey-200)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              background:
                'var(--nj-semantic-color-background-neutral-primary-default)',
            }}
          >
            <NJButton
              emphasis="subtle"
              label="Annuler"
              type="button"
              onClick={onClose}
            />
            <NJButton
              emphasis="bold"
              label={isSubmitting ? 'Création…' : 'Valider'}
              type="submit"
              disabled={isSubmitting}
            />
          </footer>
        </form>
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Curve table
// ---------------------------------------------------------------------------

type CurveErrors = ReturnType<typeof useForm<AssetCreateFormValues>>['formState']['errors']['curve'];

function CurveTable({
  fields,
  register,
  errors,
  onRemoveRow,
}: {
  fields: { id: string }[];
  register: ReturnType<typeof useForm<AssetCreateFormValues>>['register'];
  errors: CurveErrors;
  onRemoveRow: (index: number) => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--nj-core-color-grey-200)',
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 36px',
          background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
          borderBottom: '1px solid var(--nj-core-color-grey-200)',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--nj-semantic-color-text-secondary-default)',
        }}
      >
        <div style={{ padding: '8px 12px' }}>Puissance (MW)</div>
        <div style={{ padding: '8px 12px' }}>Rendement (p.u.)</div>
        <div />
      </div>
      {fields.map((field, idx) => {
        const rowError =
          errors && Array.isArray(errors)
            ? (errors as Array<{
                power?: { message?: string };
                efficiency?: { message?: string };
              }>)[idx]
            : undefined;
        return (
          <div
            key={field.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 36px',
              borderBottom: '1px solid var(--nj-core-color-grey-100)',
              alignItems: 'center',
            }}
          >
            <div style={{ padding: '4px 8px' }}>
              <input
                type="number"
                step="0.01"
                {...register(`curve.${idx}.power` as const, {
                  valueAsNumber: true,
                })}
                style={inputStyle(!!rowError?.power)}
              />
              {rowError?.power?.message && (
                <span style={errorTextStyle}>{rowError.power.message}</span>
              )}
            </div>
            <div style={{ padding: '4px 8px' }}>
              <input
                type="number"
                step="0.01"
                {...register(`curve.${idx}.efficiency` as const, {
                  valueAsNumber: true,
                })}
                style={inputStyle(!!rowError?.efficiency)}
              />
              {rowError?.efficiency?.message && (
                <span style={errorTextStyle}>{rowError.efficiency.message}</span>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <NJIconButton
                icon="delete"
                label="Supprimer le point"
                variant="tertiary"
                scale="sm"
                isDisabled={fields.length <= 2}
                onClick={() => onRemoveRow(idx)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const inputStyle = (hasError: boolean): React.CSSProperties => ({
  width: '100%',
  padding: '6px 8px',
  border: `1px solid ${
    hasError ? 'var(--nj-semantic-color-border-danger-default, #d22)' : 'var(--nj-core-color-grey-300)'
  }`,
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
  background: 'white',
});

const errorTextStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: 'var(--nj-semantic-color-text-danger-default, #c00)',
  marginTop: 2,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function assetTypeArticle(t: AssetType): string {
  switch (t) {
    case 'boiler':
      return 'une';
    case 'heat-pump':
      return 'une';
    case 'storage':
      return 'un';
    case 'solar':
      return 'un';
  }
}

function BackdropSpinner({ label }: { label: string }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(255,255,255,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 8,
        zIndex: 1200,
      }}
    >
      <NJSpinner />
      <NJText scale="sm" variant="tertiary">
        {label}
      </NJText>
    </div>
  );
}

function BackdropError({ message }: { message: string }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(255,255,255,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1200,
      }}
    >
      <NJInlineMessage variant="error">{message}</NJInlineMessage>
    </div>
  );
}
